import React, { useMemo } from "react";
import { CardContent, Collapse, Divider, GridLegacy, Typography, Alert } from "@mui/material";
import TranslationWrapper from "../../../components/TranslationWrapper/TranslationWrapper";
import GridContainer from "../../../components/Grid/GridContainer";
import FormSwitchTC from "../../../components/Forms/FormSwitchTC";
import FormSelectTC from "../../../components/Forms/FormSelectTC";
import FormTextTC from "../../../components/Forms/FormTextTC";
import { useWatch } from "react-hook-form";
import AutoSyncSwitch from "./Api/PosSyncs/AutoSyncSwitch";
import { convertObjectToSelectOptions } from "../../../templateHelpers";
import AuthenticationWrapper from "../../../auth/AuthenticationWrapper";
import FormTimePicker from "../../../components/Forms/FormTimePicker";

//defining Trivec sort option modes
const TRIVEC_SORT_OPTIONS_MODE_OFF = 0;
const TRIVEC_SORT_OPTIONS_MODE_ROW = 1;
const TRIVEC_SORT_OPTIONS_MODE_COLUMN = 2;

function MenuSyncSettings({ v2Only }) {
  const syncv2 = useWatch({ name: "extra_settings__use_menu_sync_v2" });
  const useOptionGroups = useWatch({ name: "extra_settings__use_option_groups" });
  const apiProvider = useWatch({ name: "apiProvider" });

  const customData = useWatch({ name: "custom_data" });
  const overwriteSortKeysOfOptions = customData?.syncv2?.overwrite_sort_keys_of_option_groups ?? false;
  const overwriteTagsOfProducts = customData?.syncv2?.overwrite_tags_of_products ?? false;
  const createCategoryOpeningHours = customData?.syncv2?.create_category_opening_hours ?? true;

  // Option sort modes
  let optionSortModeObj = null;
  switch (apiProvider) {
    case "TRIVECLITE":
      optionSortModeObj = [
        {
          value: TRIVEC_SORT_OPTIONS_MODE_OFF,
          label: <TranslationWrapper tDefault={"None"} tKey={"vestigingen.form.trivec.sort_options_mode_off"} />,
        },
        {
          value: TRIVEC_SORT_OPTIONS_MODE_ROW,
          label: (
            <TranslationWrapper
              tDefault={"ROW (Sorted per row, according POS Addon Page settings)"}
              tKey={"vestigingen.form.trivec.sort_options_mode_row"}
            />
          ),
        },
        {
          value: TRIVEC_SORT_OPTIONS_MODE_COLUMN,
          label: (
            <TranslationWrapper
              tDefault={"COL (Sorted per column, according POS Addon Page settings)"}
              tKey={"vestigingen.form.trivec.sort_options_mode_column"}
            />
          ),
        },
      ];
      break;
    default:
      optionSortModeObj = null;
  }

  //Convert option sort modes
  const optionSortModes = useMemo(
    () => (optionSortModeObj ? convertObjectToSelectOptions(optionSortModeObj) : null),
    []
  );

  return (
    <>
      <GridLegacy container spacing={1} style={{ marginTop: "5px", maxWidth: 1400 }}>
        <AuthenticationWrapper
          child={
            <div>
              <h4>
                <TranslationWrapper
                  tDefault={"Automatically restore products blocked by the order checker."}
                  tKey={"orderchecker.restore_blocked.description"}
                />
              </h4>
              <Alert severity={"info"}>
                <TranslationWrapper
                  tDefault={
                    "If both time settings are filled in, the system will use whichever time comes first to unblock the products."
                  }
                  tKey={"orderchecker.restore_blocked.example"}
                />
              </Alert>
            </div>
          }
          permissionKey={"custom_data.orderchecker.restore_blocked_delay"}
        />

        <FormTimePicker
          name={"custom_data.orderchecker.restore_blocked_delay"}
          tDefault={"Block duration. (Unblock automatically after a certain amount of time)"}
          tKey={"orderchecker.restore_blocked.delay_label"}
          timeFormat={"HH:mm"}
        />
        <FormTimePicker
          name={"custom_data.orderchecker.restore_blocked_time"}
          tDefault={"Daily unblock time. (Unblock automatically at a daily fixed time)"}
          tKey={"orderchecker.restore_blocked.specific_time_label"}
          timeFormat={"HH:mm"}
        />
      </GridLegacy>
      <CardContent>
        <GridContainer>
          <h3>
            <TranslationWrapper tDefault={"Menu Synchronisation Settings"} tKey={"Menu Synchronisation Settings"} />
          </h3>
          {!v2Only ? <FormSwitchTC name={"extra_settings__use_menu_sync_v2"} tKey={"Use menu sync v2"} /> : null}
          {!!syncv2 || v2Only ? (
            <>
              <FormSwitchTC
                name={"extra_settings__blocked_value_of_new_products"}
                tKey={"Blocked default value for new products"}
              />
              <FormSwitchTC
                name={"extra_settings__overwrite_position_of_categories"}
                tKey={"Overwrite position of categories"}
              />
              <FormSwitchTC
                name={"extra_settings__overwrite_sort_keys_of_categories"}
                tKey={"Overwrite order of categories"}
              />
              <FormSwitchTC
                name={"extra_settings__overwrite_position_of_products"}
                tKey={"Overwrite position of products"}
              />
              <FormSwitchTC
                name={"extra_settings__overwrite_sort_keys_of_products"}
                tKey={"Overwrite order of products"}
              />
              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.syncv2.overwrite_sort_keys_of_option_groups"}
                tKey={"Overwrite order of option groups"}
              />
              <Collapse in={overwriteSortKeysOfOptions} sx={{ width: 1 }}>
                <FormSwitchTC
                  defaultValue={false}
                  name={"custom_data.syncv2.overwrite_product_option_group_pivots"}
                  tKey={"Overwrite product option group pivot sort keys"}
                />
              </Collapse>

              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.syncv2.overwrite_category_catalog"}
                tKey={"Overwrite category catalog (food/drink)"}
              />

              {optionSortModeObj !== null ? (
                <FormSelectTC
                  name={"extra_settings__optionSortMode"}
                  options={optionSortModes}
                  tDefault={"Options sort mode"}
                  tKey={"vestigingen.form.overige.label.sortModes"}
                />
              ) : null}
              <FormSwitchTC
                name={"extra_settings__delete_unknown_categories"}
                tKey={"Delete categories which are not found in the POS"}
              />
              <FormSwitchTC
                name={"extra_settings__delete_unknown_products"}
                tKey={"Delete products which are not found in the POS"}
              />
              <FormSwitchTC
                name={"extra_settings__overwrite_allergens_of_products"}
                tKey={"Overwrite allergen information"}
              />
              <FormTextTC
                name={"extra_settings__category_name_filter"}
                tKey={"Only add categories with following text"}
              />
              {apiProvider == "TRIVECLITE" ? (
                <Typography>
                  <TranslationWrapper
                    tKey={"trivec_filter.Info Column"}
                    tDefault={"Applied on info column in Trivec"}
                  />
                </Typography>
              ) : null}

              {apiProvider === "DELIVERECT" ? (
                <>
                  <FormSwitchTC
                    name={"custom_data.syncv2.create_category_opening_hours"}
                    tKey={"Create category opening hours"}
                    defaultValue={true}
                  />
                  {createCategoryOpeningHours ? (
                    <span style={{ marginLeft: "15px" }}>
                      <FormSwitchTC
                        name={"custom_data.syncv2.overwrite_category_opening_hours"}
                        tKey={"Overwrite opening hours for existing categories"}
                        defaultValue={false}
                      />
                    </span>
                  ) : null}
                </>
              ) : null}

              {apiProvider !== "SIMPHONY" ? (
                <>
                  <FormSwitchTC name={"extra_settings__use_option_groups"} tKey={"Use option groups"} />
                  <Typography>
                    <TranslationWrapper
                      tDefault={"Switching using option groups is dangerous, only use at first synchronisation."}
                      tKey={"use_option_groups_use_at_own_risk"}
                    />
                  </Typography>
                  {apiProvider !== "TWEAKWISE" ? (
                    <Collapse in={!!useOptionGroups} sx={{ width: 1 }}>
                      <FormSwitchTC
                        defaultValue={false}
                        name={"custom_data.syncv2.duplicate_options_per_product"}
                        tKey={"Duplicate options per product"}
                      />
                    </Collapse>
                  ) : null}
                </>
              ) : null}
              {apiProvider === "TRIVECLITE" ? (
                <FormSwitchTC name={"custom_data.syncv2.use_nested_options"} tKey={"Use nested options"} />
              ) : null}
              {apiProvider === "SIMPHONY" ? (
                <FormSwitchTC
                  defaultValue={true}
                  name={"custom_data.syncv2.duplicate_options_per_product"}
                  tKey={"Duplicate options per product"}
                />
              ) : null}
              {["SIMPHONY", "UNTILL", "LIGHTSPEEDK"].includes(apiProvider) ? (
                <>
                  <FormSwitchTC
                    defaultValue={false}
                    name={"custom_data.syncv2.overwrite_tags_of_products"}
                    tKey={"Overwrite tags of products"}
                  />
                  <Collapse in={overwriteTagsOfProducts}>
                    <FormSwitchTC
                      defaultValue={false}
                      name={"custom_data.syncv2.overwrite_position_of_tags"}
                      tKey={"Overwrite position of tags"}
                    />

                    <FormSwitchTC
                      defaultValue={false}
                      name={"custom_data.syncv2.delete_unknown_tags"}
                      tKey={"Delete tags which are not found in the POS"}
                    />
                  </Collapse>
                </>
              ) : null}
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.syncv2.deactivate_added_options_for_synced_products"}
                tKey={"Deactivate added options for synced products"}
              />
              <FormSwitchTC name={"useProductStock"} tKey={"Use product stock"} />
              <FormSwitchTC name={"extra_settings__only_sync_linked_products"} tKey={"Only sync linked products"} />
              <AutoSyncSwitch />

              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.syncv2.create_new_products"}
                tKey={"Create new products if found in POS"}
              />
              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.syncv2.create_new_product_groups"}
                tKey={"Create new product groups if found in POS"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.syncv2.skip_ean_syncing"}
                tKey={"Don't sync EAN codes"}
              />
            </>
          ) : null}
        </GridContainer>
        <Divider />
      </CardContent>
    </>
  );
}

export default MenuSyncSettings;
